import { inE2E } from './environment'

// HubSpot Conversations chat widget (portal 39763539).
//
// Breakout is an SPA, so HubSpot's portal-side URL targeting can't reliably
// show/hide the widget on client-side navigation. We therefore load it in
// JS-controlled mode (`loadImmediately: false`) and drive load/remove/refresh
// ourselves from HubSpotChatController.
//
// The set of domains this touches on the host page (loader + conversations
// iframe + api/exceptions XHR) is allowlisted in the CSP in firebase.json.

const HUBSPOT_PORTAL_ID = '39763539'
const HUBSPOT_LOADER_SRC = `//js-na1.hs-scripts.com/${HUBSPOT_PORTAL_ID}.js`
const HUBSPOT_LOADER_ID = 'hs-script-loader'

type HubSpotWidget = {
  load: (options?: { widgetOpen?: boolean }) => void
  remove: () => void
  open: () => void
  close: () => void
  refresh: (options?: { openToNewThread?: boolean }) => void
  status: () => { loaded: boolean; pending: boolean }
}

declare global {
  interface Window {
    hsConversationsSettings?: { loadImmediately?: boolean }
    hsConversationsOnReady?: Array<() => void>
    HubSpotConversations?: { widget: HubSpotWidget }
  }
}

let scriptInjected = false

/**
 * Inject the HubSpot loader script in JS-controlled mode. Idempotent, and a
 * no-op in E2E runs (we don't want a third-party iframe and its network calls
 * making tests flaky). Safe to call repeatedly; only the first call injects.
 */
export function installHubSpotChat() {
  if (inE2E()) return
  if (scriptInjected) return
  if (typeof document === 'undefined') return
  if (document.getElementById(HUBSPOT_LOADER_ID)) {
    scriptInjected = true
    return
  }

  // Must be set before the loader script executes so the widget does not
  // auto-load; the controller decides when the launcher appears.
  window.hsConversationsSettings = {
    ...window.hsConversationsSettings,
    loadImmediately: false,
  }

  const script = document.createElement('script')
  script.type = 'text/javascript'
  script.id = HUBSPOT_LOADER_ID
  script.async = true
  script.defer = true
  script.src = HUBSPOT_LOADER_SRC
  document.head.appendChild(script)
  scriptInjected = true
}

/**
 * Run `fn` once the Conversations SDK is available. If it's already loaded we
 * run immediately; otherwise we queue via `hsConversationsOnReady`, which the
 * SDK drains once on startup.
 */
function whenReady(fn: () => void) {
  if (window.HubSpotConversations) {
    fn()
    return
  }
  window.hsConversationsOnReady = window.hsConversationsOnReady ?? []
  window.hsConversationsOnReady.push(fn)
}

/** Show the launcher. No-op if it's already loaded or currently loading. */
export function loadHubSpotWidget() {
  whenReady(() => {
    const widget = window.HubSpotConversations?.widget
    if (!widget) return
    const { loaded, pending } = widget.status()
    if (loaded || pending) return
    widget.load()
  })
}

/**
 * Remove the launcher/iframe. No-op if it never loaded — avoids the SDK's
 * "widget.load() has not been called" console warning when we hit an excluded
 * surface before the widget was ever shown.
 */
export function removeHubSpotWidget() {
  whenReady(() => {
    const widget = window.HubSpotConversations?.widget
    if (!widget) return
    const { loaded, pending } = widget.status()
    if (!loaded && !pending) return
    widget.remove()
  })
}

/**
 * Refresh the widget for the current URL. Recommended by HubSpot on SPA
 * navigation. No-op until the widget is actually loaded.
 */
export function refreshHubSpotWidget() {
  whenReady(() => {
    const widget = window.HubSpotConversations?.widget
    if (!widget) return
    if (!widget.status().loaded) return
    widget.refresh()
  })
}
